import React, { useState, useEffect } from 'react';
import { ref, onValue, get, set, remove, push } from 'firebase/database';
import { db } from '../../firebase';
import { Student, Course, Grade } from '../../types';

const degreeLabels: Record<string, string> = {
  bachelor: 'Lisans', master: 'Yüksek Lisans', doctorate: 'Doktora',
};

const letterGrades = ['AA', 'BA', 'BB', 'CB', 'CC', 'DC', 'DD', 'FF', 'EX', 'P', 'F', 'I', 'W'];

interface StudentLink {
  title: string;
  url: string;
  addedAt: number;
}

interface Props { courses: Course[]; }

const StudentsTab: React.FC<Props> = ({ courses }) => {
  const [students, setStudents] = useState<Student[]>([]);
  const [selected, setSelected] = useState<Student | null>(null);
  const [enrolledIds, setEnrolledIds] = useState<string[]>([]);
  const [grades, setGrades] = useState<Record<string, Grade>>({});
  const [editingCourseId, setEditingCourseId] = useState<string | null>(null);
  const [gradeForm, setGradeForm] = useState({ midterm: '', final: '', letterGrade: '' });
  const [saving, setSaving] = useState(false);
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [addingCourseId, setAddingCourseId] = useState('');
  const [adding, setAdding] = useState(false);
  const [studentLinks, setStudentLinks] = useState<StudentLink[]>([]);
  const [linkForm, setLinkForm] = useState({ title: '', url: '' });
  const [addingLink, setAddingLink] = useState(false);

  useEffect(() => {
    return onValue(ref(db, 'students'), snap => {
      const d = snap.val();
      setStudents(d ? Object.values(d) as Student[] : []);
    });
  }, []);

  const selectStudent = async (s: Student) => {
    setSelected(s);
    setEditingCourseId(null);
    setShowAddCourse(false);
    setAddingCourseId('');
    setAddingLink(false);
    setLinkForm({ title: '', url: '' });

    const [enrollSnap, gradeSnap, linkSnap] = await Promise.all([
      get(ref(db, `enrollments/${s.uid}`)),
      get(ref(db, `grades/${s.uid}`)),
      get(ref(db, `studentLinks/${s.uid}`)),
    ]);

    setEnrolledIds(enrollSnap.val() ? Object.keys(enrollSnap.val()) : []);
    setGrades(gradeSnap.val() || {});
    setStudentLinks(linkSnap.val() ? Object.values(linkSnap.val()) as StudentLink[] : []);
  };

  const openGradeEdit = (courseId: string) => {
    const existing = grades[courseId];
    setGradeForm({
      midterm: existing?.midterm !== undefined ? String(existing.midterm) : '',
      final: existing?.final !== undefined ? String(existing.final) : '',
      letterGrade: existing?.letterGrade || '',
    });
    setEditingCourseId(courseId);
  };

  const saveGrade = async (courseId: string) => {
    if (!selected) return;
    const course = courses.find(c => c.id === courseId);
    if (!course) return;
    setSaving(true);

    const grade: any = {
      courseId,
      courseCode: course.code,
      courseName: course.name,
      credits: course.credits,
      semester: course.semester,
    };
    if (gradeForm.midterm !== '') grade.midterm = Number(gradeForm.midterm);
    if (gradeForm.final !== '') grade.final = Number(gradeForm.final);
    if (gradeForm.letterGrade) grade.letterGrade = gradeForm.letterGrade;

    await set(ref(db, `grades/${selected.uid}/${courseId}`), grade);
    setGrades(prev => ({ ...prev, [courseId]: grade }));
    setEditingCourseId(null);
    setSaving(false);
  };

  const handleAddCourse = async () => {
    if (!selected || !addingCourseId) return;
    setAdding(true);
    await set(ref(db, `enrollments/${selected.uid}/${addingCourseId}`), true);
    setEnrolledIds(prev => [...prev, addingCourseId]);
    setAddingCourseId('');
    setShowAddCourse(false);
    setAdding(false);
  };

  const handleRemoveCourse = async (courseId: string) => {
    if (!selected) return;
    if (!window.confirm('Bu öğrencinin ders kaydını silmek istediğinizden emin misiniz?')) return;
    await remove(ref(db, `enrollments/${selected.uid}/${courseId}`));
    setEnrolledIds(prev => prev.filter(id => id !== courseId));
  };

  const handleAddLink = async () => {
    if (!selected || !linkForm.title.trim() || !linkForm.url.trim()) return;
    const link: StudentLink = { title: linkForm.title.trim(), url: linkForm.url.trim(), addedAt: Date.now() };
    await push(ref(db, `studentLinks/${selected.uid}`), link);
    setStudentLinks(prev => [...prev, link]);
    setLinkForm({ title: '', url: '' });
    setAddingLink(false);
  };

  const handleDeleteLink = async (linkUrl: string) => {
    if (!selected) return;
    const snap = await get(ref(db, `studentLinks/${selected.uid}`));
    const entries: any[] = snap.val() ? Object.entries(snap.val()) : [];
    const entry = entries.find(([, v]: any) => v.url === linkUrl);
    if (entry) await remove(ref(db, `studentLinks/${selected.uid}/${entry[0]}`));
    setStudentLinks(prev => prev.filter(l => l.url !== linkUrl));
  };

  const isYouTube = (url: string) => url.includes('youtube.com') || url.includes('youtu.be');

  const enrolledCourses = courses.filter(c => c.id && enrolledIds.includes(c.id));
  const availableCourses = courses.filter(c => c.id && !enrolledIds.includes(c.id));

  return (
    <>
      <h2 className="admin-page-title">Öğrenciler</h2>
      <p className="admin-page-sub">{students.length} kayıtlı öğrenci</p>

      <div className="a-students-layout">
        {/* Öğrenci Listesi */}
        <div className="admin-card a-student-list-card">
          <div className="admin-card-header"><h3>Öğrenci Listesi</h3></div>
          {students.length === 0 ? (
            <p className="a-empty" style={{ padding: 16 }}>Kayıtlı öğrenci bulunmamaktadır.</p>
          ) : students.map(s => (
            <div
              key={s.uid}
              className={`a-student-item ${selected?.uid === s.uid ? 'active' : ''}`}
              onClick={() => selectStudent(s)}
            >
              <div className="a-student-avatar">{s.fullName.charAt(0).toUpperCase()}</div>
              <div>
                <div className="a-student-name">{s.fullName}</div>
                <div className="a-student-meta">{s.studentId} · {s.program}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Öğrenci Detay */}
        <div className="a-student-detail">
          {!selected ? (
            <div className="admin-card">
              <p className="a-empty" style={{ padding: 32 }}>Sol listeden bir öğrenci seçin.</p>
            </div>
          ) : (
            <>
              {/* Profil */}
              <div className="admin-card" style={{ marginBottom: 16 }}>
                <div className="admin-card-header"><h3>{selected.fullName}</h3></div>
                <div className="a-student-info-grid">
                  <div><label>Öğrenci No</label><p>{selected.studentId}</p></div>
                  <div><label>E-posta</label><p>{selected.email}</p></div>
                  <div><label>Program</label><p>{selected.program}</p></div>
                  <div><label>Düzey</label><p>{degreeLabels[selected.degree] || selected.degree}</p></div>
                </div>
              </div>

              {/* Öğrenci Linkleri / Belgeler */}
              <div className="admin-card" style={{ marginBottom: 16 }}>
                <div className="admin-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h3>Öğrenci Belgeleri & Linkleri</h3>
                  <button
                    className="a-btn a-btn-add"
                    style={{ margin: 0, padding: '5px 12px', fontSize: '0.82rem' }}
                    onClick={() => setAddingLink(v => !v)}
                  >
                    + Link Ekle
                  </button>
                </div>

                {addingLink && (
                  <div className="a-link-form" style={{ padding: '10px 16px' }}>
                    <input
                      className="a-search"
                      placeholder="Başlık (örn: Transkript, Diploma vb.)"
                      value={linkForm.title}
                      onChange={e => setLinkForm(f => ({ ...f, title: e.target.value }))}
                    />
                    <input
                      className="a-search"
                      placeholder="URL (Drive, Dropbox, YouTube vb.)"
                      value={linkForm.url}
                      onChange={e => setLinkForm(f => ({ ...f, url: e.target.value }))}
                    />
                    <button className="a-btn a-btn-save" onClick={handleAddLink}>Ekle</button>
                    <button className="a-btn a-btn-cancel" onClick={() => { setAddingLink(false); setLinkForm({ title: '', url: '' }); }}>İptal</button>
                  </div>
                )}

                <div className="a-material-list" style={{ padding: '10px 16px' }}>
                  {studentLinks.length === 0 ? (
                    <span className="a-material-empty">Henüz belge/link eklenmedi</span>
                  ) : studentLinks.map((l, i) => (
                    <div className="a-material-item" key={i}>
                      <span>{isYouTube(l.url) ? '▶️' : '🔗'}</span>
                      <a href={l.url} target="_blank" rel="noreferrer" className="a-material-name">{l.title}</a>
                      <button className="a-material-del" onClick={() => handleDeleteLink(l.url)}>✕</button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dersler & Notlar */}
              <div className="admin-card">
                <div className="admin-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h3>Kayıtlı Dersler & Notlar</h3>
                  {availableCourses.length > 0 && (
                    <button
                      className="a-btn a-btn-add"
                      style={{ margin: 0, padding: '5px 12px', fontSize: '0.82rem' }}
                      onClick={() => setShowAddCourse(v => !v)}
                    >
                      + Ders Ata
                    </button>
                  )}
                </div>

                {showAddCourse && (
                  <div className="a-assign-course-bar">
                    <select className="a-select" value={addingCourseId} onChange={e => setAddingCourseId(e.target.value)}>
                      <option value="">Ders Seçin</option>
                      {availableCourses.map(c => (
                        <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                      ))}
                    </select>
                    <button className="a-btn a-btn-save" onClick={handleAddCourse} disabled={!addingCourseId || adding}>
                      {adding ? 'Ekleniyor...' : 'Ekle'}
                    </button>
                    <button className="a-btn a-btn-cancel" onClick={() => { setShowAddCourse(false); setAddingCourseId(''); }}>İptal</button>
                  </div>
                )}

                {enrolledCourses.length === 0 ? (
                  <p className="a-empty" style={{ padding: 16 }}>Bu öğrenciye henüz ders atanmamış.</p>
                ) : (
                  <div className="a-table-wrap">
                    <table>
                      <thead>
                        <tr><th>Kod</th><th>Ders</th><th>Kredi</th><th>Vize</th><th>Final</th><th>Not</th><th></th></tr>
                      </thead>
                      <tbody>
                        {enrolledCourses.map(c => {
                          const g = grades[c.id!];
                          const isEditing = editingCourseId === c.id;
                          return (
                            <tr key={c.id}>
                              <td className="a-code-cell">{c.code}</td>
                              <td className="a-name-cell">{c.name}</td>
                              <td>{c.credits}</td>
                              {isEditing ? (
                                <>
                                  <td>
                                    <input className="a-grade-input" type="number" min={0} max={100}
                                      value={gradeForm.midterm}
                                      onChange={e => setGradeForm(f => ({ ...f, midterm: e.target.value }))}
                                      placeholder="0-100" />
                                  </td>
                                  <td>
                                    <input className="a-grade-input" type="number" min={0} max={100}
                                      value={gradeForm.final}
                                      onChange={e => setGradeForm(f => ({ ...f, final: e.target.value }))}
                                      placeholder="0-100" />
                                  </td>
                                  <td>
                                    <select className="a-select a-grade-select" value={gradeForm.letterGrade}
                                      onChange={e => setGradeForm(f => ({ ...f, letterGrade: e.target.value }))}>
                                      <option value="">—</option>
                                      {letterGrades.map(l => <option key={l} value={l}>{l}</option>)}
                                    </select>
                                  </td>
                                  <td style={{ whiteSpace: 'nowrap' }}>
                                    <button className="a-btn a-btn-save" disabled={saving} onClick={() => saveGrade(c.id!)}>
                                      {saving ? '...' : 'Kaydet'}
                                    </button>
                                    <button className="a-btn a-btn-cancel" style={{ marginLeft: 4 }} onClick={() => setEditingCourseId(null)}>İptal</button>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td>{g?.midterm !== undefined ? g.midterm : '—'}</td>
                                  <td>{g?.final !== undefined ? g.final : '—'}</td>
                                  <td>{g?.letterGrade ? <span className="badge b-grade">{g.letterGrade}</span> : '—'}</td>
                                  <td style={{ whiteSpace: 'nowrap' }}>
                                    <button className="a-btn a-btn-detail" onClick={() => openGradeEdit(c.id!)}>Not Gir</button>
                                    <button className="a-btn a-btn-delete" style={{ marginLeft: 4 }} onClick={() => handleRemoveCourse(c.id!)}>Sil</button>
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default StudentsTab;
